import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { useBuyArenaOfferMutation } from '@/api/queries/shop';
import { ToastNotification } from '@/common/components/ToastNotification/ToastNotification';
import { additionalDiscountForUsdt } from '@/common/config/common';
import { useStorePrice } from '@/common/hooks/apple-price';
import { usePaymentStatus } from '@/common/hooks/payment-status';
import { usePurchaseAttempt } from '@/common/hooks/purchase-attempt';
import { useTonAutoConnect } from '@/common/hooks/ton-auto-connect';
import { multiplication } from '@/common/lib/decimal';
import { Playdeck, SendAnalitics } from '@/common/lib/playdeck';
import { type ActivateVipStatusResponseType } from '@/common/types/query/user';
import { type ArenaOffersDto } from '@/common/types/shop/marketBoxes';
import { Confetti } from '@/components/Animations/Confetti/Confetti';
import { arenaOffersInfo } from '@/components/OffersMarket/ArenaOffers/config';
import SwitchCardsButton from '@/components/OffersMarket/ArenaOffers/ProfitDrop/SwitchCardsButton';
import RewardArray from '@/components/OffersMarket/ArenaOffers/RewardArray';
import { StringMain } from '@/components/Typography/Typography';
import { GreyBlurButton } from '@/components/ui/Button/Button';
import { Close } from '@/components/ui/Close/Close';
import TONandStarsBuyButtons from '@/components/ui/TONandStarsBuyButtons/TONandStarsBuyButtons';
import ClaimStatusProcess from '@/components/ui/ViewStatusComponents/ClaimStatusProcess/ClaimStatusProcess';
import PendingStatus from '@/components/ui/ViewStatusComponents/PendingStatus';
import SuccessfullStatus from '@/components/ui/ViewStatusComponents/SuccessfullStatus';
import { changePaymentStatus } from '@/redux/reducers/paymentsReducer';
import { subtractBalance } from '@/redux/reducers/userReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import { ModalContext } from '../Modal/config';

import ProductTitleDefault from './ProductTitle/ProductTitleDefault';
import ProductTitleProfitDrop from './ProductTitle/ProductTitleProfitDrop';

import coinPng from '/img/tgem.png';
import coinWebp from '/img/tgem.webp';

interface prop {
  arenaOffer: ArenaOffersDto;
  closeCallback?: () => void;
  // opened by the game itself (unfinished offer), not by the player's tap
  autoOpened?: boolean;
}

const BuyArenaOffer: React.FC<prop> = ({
  arenaOffer,
  closeCallback,
  autoOpened,
}) => {
  const activeCard = useAppSelector(
    (state) => state.shop.activeProfitDropOffer,
  );
  const profitOffer = ['profit_drop_small', 'profit_drop_large'].includes(
    arenaOffer.type,
  );
  const { id, name, reward, cost, isBuyingProcess } =
    profitOffer && activeCard ? activeCard : arenaOffer;
  const notCompletedProfitDrop: boolean = profitOffer && isBuyingProcess;
  const paymentStatus = usePaymentStatus();
  const connectWalletAndDoTransaction = useTonAutoConnect();
  const [buyOffer, { isLoading }] = useBuyArenaOfferMutation();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { closeModal, secondModal, thirdModal } = useContext(ModalContext);
  const info = arenaOffersInfo[name];
  const isDiscount: boolean = cost.has_discount;
  const prices = isDiscount ? cost.discounted : cost.base;
  const usdPrice = useMemo(
    () => multiplication(prices.usd, additionalDiscountForUsdt.coefficient),
    [prices],
  );

  usePurchaseAttempt({
    item: name,
    item_id: id,
    price: prices.usd,
    currency: 'usdt',
  });

  const sendAnalitics = (currency: 'ton' | 'xtr' | 'usdt'): void => {
    SendAnalitics.purchaseSuccessfull({
      item: name,
      item_id: id,
      price: prices.usd,
      currency,
    });
  };

  const onClick = async (currency: 'XTR' | 'TON'): Promise<void> => {
    if (profitOffer) {
      localStorage.setItem('profit-drop-rewards', JSON.stringify(reward));
    }

    if (currency === 'TON') {
      await new Promise<void>((resolve, reject) => {
        connectWalletAndDoTransaction(
          async () =>
            (await buyOffer({
              offerId: id,
              currency,
            }).unwrap()) as ActivateVipStatusResponseType,
          {
            onSuccess: () => {
              ToastNotification(t('Pending Transaction'), {
                img: coinPng,
                webp: coinWebp,
              });
              resolve();
              dispatch(changePaymentStatus('pending'));
              sendAnalitics('ton');
            },
            onError: () => {
              ToastNotification(t('Something went wrong, please try again'), {
                img: coinPng,
                webp: coinWebp,
              });
              reject(new Error('Transaction failed'));
              dispatch(changePaymentStatus(undefined));
            },
          },
        );
      });
    } else {
      const response = await buyOffer({
        offerId: id,
        currency,
      }).unwrap();

      // for ActivateVipStatusBuyLinkResponseType response type
      if ('link' in response) {
        await new Promise<void>((resolve, reject) => {
          Playdeck.requestPayment({
            amount: prices.stars,
            description: 'buy arena offer',
            externalId: `buy-arena-offer-${prices.stars}`,
          });
          Telegram.WebApp.openInvoice(response.link, (status) => {
            if (status === 'cancelled' || status === 'failed') {
              ToastNotification(t('Something went wrong, please try again'), {
                img: coinPng,
                webp: coinWebp,
              });
              reject(new Error(`Payment ${status}`));
            } else {
              resolve();
              dispatch(changePaymentStatus('pending'));
              sendAnalitics('xtr');
            }
            console.log(`payment status ${status}`);
          });
        });
      }
    }
  };

  const buyUsdt = async (): Promise<void> => {
    if (profitOffer) {
      localStorage.setItem('profit-drop-rewards', JSON.stringify(reward));
    }

    try {
      await buyOffer({ offerId: id, currency: 'USDT' }).unwrap();

      if (thirdModal.isOpen) {
        thirdModal.closeModal();
      } else {
        secondModal.closeModal();
      }
      dispatch(changePaymentStatus('pending'));
      sendAnalitics('usdt');
      //aditional discount 5%
      dispatch(
        subtractBalance({
          type: 'usdt',
          value: usdPrice,
        }),
      );
    } catch (error) {
      console.error(error);
      ToastNotification(t('Something went wrong, please try again'), {
        img: coinPng,
        webp: coinWebp,
      });
      dispatch(changePaymentStatus(undefined));
    }
  };

  const isSuccess = paymentStatus === 'success' || notCompletedProfitDrop;

  const handleCloseModal = (): void => {
    closeModal();
    secondModal.closeModal();

    if (closeCallback) closeCallback();
    dispatch(changePaymentStatus(undefined));
  };

  const storePrice = useStorePrice(prices.usd);

  return (
    <div
      className={cn(
        'flex flex-col h-full items-center justify-center pt-[calc(var(--header-height)_+_2rem)] 720-h:pt-[calc(var(--header-height)_+_0.5rem)] gap-[32px] 720-h:gap-4',
        isSuccess && profitOffer && '!gap-0',
      )}
    >
      {paymentStatus === 'success' && (
        <Confetti
          className="-z-[1] w-full h-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          loop={false}
          autoPlay={true}
          speed={2}
          width="100%"
          height="100%"
        />
      )}
      {paymentStatus === 'pending' ? (
        <PendingStatus />
      ) : isSuccess ? (
        profitOffer ? (
          <ClaimStatusProcess />
        ) : (
          <SuccessfullStatus>
            <RewardArray
              variant="big"
              name={name}
              reward={reward}
              classNames="items-center"
            />
          </SuccessfullStatus>
        )
      ) : (
        <>
          {profitOffer ? (
            <ProductTitleProfitDrop
              info={info}
              reward={reward}
              status="notClaimed"
            />
          ) : (
            <ProductTitleDefault info={info} name={name} reward={reward} />
          )}
          <StringMain weight="bold" className="!text-[40px]">
            {storePrice ?? `$ ${prices.usd}`}
          </StringMain>
          {SwitchCardsButton && profitOffer && (
            <div className="h-[32px] w-[200px]">
              <SwitchCardsButton variant="transparent" />
            </div>
          )}
          <div className="flex flex-col self-stretch">
            <TONandStarsBuyButtons
              onClick={onClick}
              onUsdtBuy={buyUsdt}
              isLoading={isLoading}
              //aditional discount 5%
              price={prices.usd}
              usdPrice={usdPrice}
              fullPrice={cost.base.usd}
              starPrice={prices.stars}
              discount={additionalDiscountForUsdt.percentage}
              mustBeUsdtButton={true}
              variant="switch"
              autoCheckout={!autoOpened}
            />
            <GreyBlurButton
              onClick={handleCloseModal}
              className="mx-6 !w-auto mt-3"
              text={t('Close')}
            />
          </div>
        </>
      )}
      {paymentStatus === 'pending' &&
        !notCompletedProfitDrop &&
        !profitOffer && (
          <Close
            onClick={handleCloseModal}
            imageVariant="modal"
            size="lg"
            className="absolute bottom-[calc(var(--tg-safe-area-inset-bottom,0px)+30px)]"
          />
        )}
    </div>
  );
};

export default BuyArenaOffer;
